import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import { useRequestData, useLabels } from 'wsm-common-data';

import { useNavigate } from 'react-router-dom';
import { trackEvent } from 'ddc-track-event';
import Select from './Select';
import ColorSwatchRgb from './ColorSwatchRgb';
import {
	setImage,
	setPaint,
	setPackages,
	setVehicleConfiguration
} from '../actions/setVehicleConfiguration';
import {
	setEnableContinueBtn,
	setDisableContinueBtn
} from '../actions/setContinueBtn';
import {
	setSelectedTrimColorData,
	setSelectedTrimPackageData
} from '../actions/setSelectedTrimData';
import { initVehicleConfiguration } from '../server/data/defaults';
import { API } from '../api/services';
import { getResizedImageUrl } from '../helpers';
import SelectItemLoader from './SelectItemLoader';
import NoDataAvailable from './NoDataAvailable';
import {
	DEFAULT_IMAGE_WIDTH,
	DEFAULT_IMAGE_WIDTH_MOBILE_TRAY
} from '../constants';
import stepController from '../controllers/StepController';

const Color = ({ setCurrentStep }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	setCurrentStep('color');
	const labels = useLabels();

	// TODO: Dry this out.  We shouldn't be doing these same steps multiuple times across components
	const { accountData, selectedTrimData, vehicleConfiguration, rootDir } =
		useSelector((state) => state);

	const {
		options: { drivetrain, paint, packages: previousPackages },
		image
	} = vehicleConfiguration;

	const { accountId } = accountData;
	const { locale, deviceType, widgetName, windowId } = useRequestData();
	const [trimColorData, setTrimColorData] = useState([]);
	const [selectedIndex, setSelectedIndex] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const isMobile = deviceType === 'MOBILE';

	const handleSelect = (_, index) => {
		const { image: colorImage, name } = trimColorData[index];
		setSelectedIndex(index);
		dispatch(setEnableContinueBtn(true));

		if (colorImage) {
			const imageUrl = getResizedImageUrl(
				colorImage,
				DEFAULT_IMAGE_WIDTH
			);
			dispatch(setImage(imageUrl));
		}
		dispatch(
			setPaint({
				value: name,
				keyLabel: name,
				valueLabel: '—',
				modified: true
			})
		);

		// do not reset packages if this is the same trim as previous
		const totalPreviousSelectedPackages =
			previousPackages?.selections?.length || 0;
		let totalIdenticalPackagesFound = 0;
		selectedTrimData?.packages?.forEach((currentPackage) => {
			previousPackages?.selections?.forEach((previousPackage) => {
				if (
					currentPackage.id === previousPackage.id &&
					currentPackage.description ===
						previousPackage.description &&
					currentPackage.msrp === previousPackage.msrp
				) {
					// found same packages in currently selected trim, don't reset
					totalIdenticalPackagesFound += 1;
				}
			});
		});

		if (
			!(
				totalPreviousSelectedPackages &&
				totalPreviousSelectedPackages === totalIdenticalPackagesFound
			)
		) {
			dispatch(
				setPackages(
					cloneDeep(initVehicleConfiguration.options.packages)
				)
			);
		}

		trackEvent(widgetName, windowId, {
			element: 'Paint option',
			elementCTA: name,
			action: 'selected',
			result: 'Paint option radio button selected'
		});
	};

	// when trim color data changes
	// set selected index to previously selected index (if it exists)
	useEffect(() => {
		if (paint && trimColorData?.length) {
			const paintIndex = trimColorData.findIndex(
				(item) => item.name === paint.value
			);

			if (paintIndex > -1) {
				setSelectedIndex(paintIndex);
				dispatch(setEnableContinueBtn(true));
			}
		}
	}, [trimColorData]);

	useEffect(() => {
		// disable continue button by default
		dispatch(setDisableContinueBtn(true));
		if (!drivetrain.value) {
			const lsVehicleConfig = stepController.getLsVehicleConfig();
			if (!lsVehicleConfig?.make || !lsVehicleConfig?.model) {
				navigate(stepController.getRootPath());
			} else {
				dispatch(setVehicleConfiguration(lsVehicleConfig));
				const trimScreenPath = `/${rootDir}/${lsVehicleConfig.make}/${lsVehicleConfig.model}.htm`;
				navigate(trimScreenPath);
			}
		} else {
			let resolveTrimsData = null;
			const options = {
				fields: ['vehicleTrimColors', 'packages']
			};
			const getGvTrimsSync = async () => {
				resolveTrimsData = await API.getGvTrims(
					vehicleConfiguration.trimId,
					accountId,
					locale,
					options
				);

				setTrimColorData(resolveTrimsData.trimColors);
				dispatch(setSelectedTrimColorData(resolveTrimsData.trimColors));
				dispatch(
					setSelectedTrimPackageData(resolveTrimsData.trimPackages)
				);

				// preload images
				resolveTrimsData.trimColors.forEach(({ image: colorImage }) => {
					if (colorImage) {
						// desktop/top view
						const img = new Image();
						img.src = getResizedImageUrl(
							colorImage,
							DEFAULT_IMAGE_WIDTH
						);
						// mobile tray
						if (isMobile) {
							const mobileImg = new Image();
							mobileImg.src = getResizedImageUrl(
								colorImage,
								DEFAULT_IMAGE_WIDTH_MOBILE_TRAY
							);
						}
					}
				});

				setIsLoading(false);
			};
			getGvTrimsSync();
		}
	}, []);

	const imageSizeStyle = {
		minHeight: '280px'
	};

	if (isLoading) {
		return isMobile ? (
			<React.Fragment>
				<div className="skeleton-shimmer" style={imageSizeStyle} />
				<div className="my-4">
					<SelectItemLoader count={5} mobile />
				</div>
			</React.Fragment>
		) : (
			<SelectItemLoader count={3} />
		);
	} else if (!isLoading && !trimColorData?.length) {
		dispatch(setEnableContinueBtn(true));
		return (
			<NoDataAvailable label={labels.get('NO_PAINT_OPTIONS_AVAILABLE')} />
		);
	}

	return (
		<React.Fragment>
			{isMobile ? (
				<React.Fragment>
					<div>
						{image && (
							<img
								className="px-3"
								src={getResizedImageUrl(
									image,
									DEFAULT_IMAGE_WIDTH
								)}
								alt={paint?.value || ''}
								style={imageSizeStyle}
							/>
						)}
					</div>
					<div className="my-4">
						{trimColorData.map((color, index) => {
							const { name } = color;
							return (
								// TODO: fix styles for mobile to adjust for a11y
								/* eslint-disable jsx-a11y/click-events-have-key-events */
								/* eslint-disable jsx-a11y/no-static-element-interactions */
								<div
									className="d-inline-block"
									key={`color_${name}`}
									onClick={() => handleSelect(null, index)}
									title={name}
								>
									<ColorSwatchRgb
										r={color.r}
										b={color.b}
										g={color.g}
										style={{
											padding: '3px',
											fontSize: '14px'
										}}
										className={`normalized-swatch-large ${
											selectedIndex === index
												? 'border-info'
												: ''
										}`}
									/>
								</div>
							);
						})}
					</div>
				</React.Fragment>
			) : (
				trimColorData.map((color, index) => {
					const data = {
						value: color.name,
						id: color.name
					};
					return (
						<Select
							data={data}
							itemIndex={index}
							inputType="radio"
							selected={selectedIndex === index}
							handleSelect={handleSelect}
							sideContent={
								<ColorSwatchRgb
									r={color.r}
									b={color.b}
									g={color.g}
									style={{ height: '36px', width: '36px' }}
									className="normalized-swatch-medium"
								/>
							}
							labelClasses="p-4"
							key={`color.select.${data.value}`}
						/>
					);
				})
			)}
		</React.Fragment>
	);
};

Color.propTypes = {
	setCurrentStep: PropTypes.func
};

export default Color;
